// subCore.js — the sound of sub:{...}: an octave pedal's sub
// -----------------------------------------------
// Pure, so it can be tested without an AudioContext; subWorklet.js runs it
// in the audio engine, sub.js puts it in a synth's voice.
//
// As an analogue octave divider: the wave's upward zero crossings (found on
// a lowpassed copy, with a little hysteresis) flip a switch -- every
// crossing for an octave down, every second for two -- making a square at
// half (a quarter) the pitch. It is given the sound's own loudness, then
// rounded by a lowpass that follows the pitch it found, into a fat, near-sine
// sub, and added under the dry sound. On one line it tracks cleanly; on a
// chord or noise it growls.

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

/**
 * The settings of sub: -- a number is the level (sub:0.8), an object the
 * lot: { level 0..2 (0.8), octave 1 | 2 (1), tone 0..1 (0.5): rounder .. brighter }.
 */
export function subSettings(cfg) {
  const c = typeof cfg === "object" && cfg ? cfg : { level: cfg };
  return {
    level: clamp(num(c.level ?? c.amount, 0.8), 0, 2),
    octave: num(c.octave, 1) >= 2 ? 2 : 1,
    tone: clamp(num(c.tone, 0.5), 0, 1),
  };
}

export class SubCore {
  constructor(sampleRate, settings = {}) {
    this.sr = sampleRate;
    this.set(settings);
    this.det = 0;              // the detector's lowpassed copy
    this.armed = false;        // below -h: an upward crossing may come
    this.since = 0;            // samples since the last upward crossing
    this.period = sampleRate / 110;
    this.count = 0;
    this.flip = 1;
    this.env = 0;
    this.fc = 100;
    this.l1 = 0; this.l2 = 0; this.l3 = 0;
  }

  set(settings) {
    this.s = subSettings(settings);
    const sr = this.sr;
    this.aDet = 1 - Math.exp(-2 * Math.PI * 900 / sr);
    this.aAtt = 1 - Math.exp(-1 / (0.002 * sr));
    this.aRel = 1 - Math.exp(-1 / (0.06 * sr));
  }

  process(input, output) {
    const { level, octave, tone } = this.s;
    const sr = this.sr;
    const every = octave === 2 ? 2 : 1;
    for (let i = 0; i < input.length; i++) {
      const x = input[i];
      // the loudness the sub is given
      const ax = Math.abs(x);
      this.env += (ax > this.env ? this.aAtt : this.aRel) * (ax - this.env);
      // upward zero crossings of a lowpassed copy, with hysteresis
      this.det += this.aDet * (x - this.det);
      const h = 0.05 * this.env;
      this.since++;
      if (this.det < -h) this.armed = true;
      else if (this.armed && this.det > h) {
        this.armed = false;
        if (this.since > 8) this.period = 0.8 * this.period + 0.2 * this.since;   // ignore jitter
        this.since = 0;
        if (++this.count >= every) { this.count = 0; this.flip = -this.flip; }
      }
      // rounded by a lowpass that follows the pitch found: near its fundamental
      const subHz = sr / Math.max(8, this.period) / (2 * every);
      const target = clamp(subHz * (1.1 + 1.4 * tone), 25, 600);
      this.fc += 0.002 * (target - this.fc);
      const a = 1 - Math.exp(-2 * Math.PI * this.fc / sr);
      const sq = this.flip * this.env;
      this.l1 += a * (sq - this.l1);
      this.l2 += a * (this.l1 - this.l2);
      this.l3 += a * (this.l2 - this.l3);
      output[i] = x + level * 1.6 * this.l3;
    }
  }
}
